import { DemoError, demoChildrenOf, demoGuardiansOf, demoMemberById, demoMembers } from "./demo.js";
import { supabase } from "./supabase.js";
import { playingFlags } from "./types.js";
import type {
  ContactDetails,
  Member,
  MemberCategory,
  MemberRole,
  MemberStatus,
  PlayingAs,
  SeedLevel,
  Sport,
} from "./types.js";

const COLUMNS =
  "id, display_name, handle, avatar_url, status, role, self_assessed, category, sports, dominant_hand, preferred_side, created_at, managed, junior, senior";

interface MemberRow {
  id: string;
  display_name: string;
  handle: string | null;
  avatar_url: string | null;
  status: MemberStatus;
  role: MemberRole;
  self_assessed: SeedLevel | null;
  category: MemberCategory | null;
  sports: Sport[] | null;
  dominant_hand: "left" | "right" | null;
  preferred_side: "left" | "right" | null;
  created_at: string;
  managed: boolean;
  junior: boolean;
  senior: boolean;
}

function toMember(row: MemberRow): Member {
  return {
    id: row.id,
    displayName: row.display_name,
    handle: row.handle,
    avatarUrl: row.avatar_url,
    status: row.status,
    role: row.role,
    selfAssessed: row.self_assessed,
    category: row.category,
    sports: row.sports ?? [],
    dominantHand: row.dominant_hand,
    preferredSide: row.preferred_side,
    createdAt: row.created_at,
    managed: row.managed,
    junior: row.junior,
    senior: row.senior,
  };
}

/**
 * The signed-in user's own membership, or null if they have not asked to join yet.
 *
 * A missing row is the normal state right after a first sign-in, not an error — signing
 * in and being a member are separate things (requirements §4).
 */
export async function fetchOwnMember(userId: string): Promise<Member | null> {
  if (!supabase) return null;

  const { data, error } = await supabase.from("members").select(COLUMNS).eq("id", userId);

  if (error) throw new Error(error.message);
  const row = data?.[0] as MemberRow | undefined;
  return row ? toMember(row) : null;
}

/**
 * Any member, by id. Null when there is no such member, or when there is one an active
 * member is not allowed to see — `members_read_community` makes those the same answer, and
 * the profile screen says "no such player" to both rather than guessing which.
 */
export async function fetchMember(memberId: string): Promise<Member | null> {
  if (!supabase) return demoMemberById(memberId);

  const { data, error } = await supabase.from("members").select(COLUMNS).eq("id", memberId);

  if (error) throw new Error(error.message);
  const row = data?.[0] as MemberRow | undefined;
  return row ? toMember(row) : null;
}

export async function fetchMembers(): Promise<Member[]> {
  if (!supabase) return demoMembers;

  const { data, error } = await supabase.from("members").select(COLUMNS).order("display_name");

  if (error) throw new Error(error.message);
  return (data as MemberRow[]).map(toMember);
}

export interface JoinRequest {
  displayName: string;
  sports: Sport[];
  selfAssessed: SeedLevel;
  category: MemberCategory;
  /** Playing as a junior or a senior — self-declared here, never both, and only an admin
   * changes either afterwards. Neither is an adult. */
  junior: boolean;
  senior: boolean;
  dominantHand: "left" | "right" | null;
  preferredSide: "left" | "right" | null;
  avatarUrl: string | null;
}

/**
 * Through the `join` Edge Function, not an insert. Whether an admin has to approve is a
 * deployment setting (`REQUIRE_APPROVAL`) that only the function can read, so it decides
 * between active and pending and writes the row with the service role. Nothing here says who
 * you are or what status you get: the function takes the id from the session and ignores any
 * status or role in the body. The caller re-reads the member afterwards to find out which.
 *
 * `userId` is kept for the call sites; the function trusts the token, not this.
 */
export async function requestToJoin(_userId: string, request: JoinRequest): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.functions.invoke("join", { body: request });

  // As in `generateDraw`: a non-2xx carries its sentence in the body, not in `error.message`.
  if (error) {
    const response = (error as { context?: Response }).context;
    const body = await response?.json().catch(() => null as { error?: string } | null);
    throw new Error(body?.error ?? error.message);
  }
}

// ---------------------------------------------------------------------------
// A flat number and a phone number.
//
// Their own table, not columns on `members`, because every active member reads every member
// row and these are for the member, organisers and admins only. RLS on `member_contacts`
// says exactly that, so a read here returns what the caller may see and nothing else. The
// only write is `set_contact_details`, which cleans what was typed and writes the caller's
// own row — see 20261001000300.
// ---------------------------------------------------------------------------

interface ContactRow {
  member_id: string;
  flat_no: string | null;
  phone: string | null;
}

const noContact: ContactDetails = { flatNo: null, phone: null };

const toContact = (row: ContactRow | undefined): ContactDetails =>
  row ? { flatNo: row.flat_no, phone: row.phone } : noContact;

/** Your own details, or neither when you hold none. */
export async function fetchOwnContact(userId: string): Promise<ContactDetails> {
  if (!supabase) return noContact;

  const { data, error } = await supabase
    .from("member_contacts")
    .select("member_id, flat_no, phone")
    .eq("member_id", userId);

  if (error) throw new Error(error.message);
  return toContact((data as ContactRow[])[0]);
}

/**
 * Everybody's, by member — the Admin screen's. One unfiltered read, so no `member_id=in.(...)`
 * list to outgrow PostgREST's URL limit; for anybody but an organiser or admin RLS makes it
 * their own row alone.
 */
export async function fetchAllContacts(): Promise<Map<string, ContactDetails>> {
  if (!supabase) return new Map();

  const { data, error } = await supabase
    .from("member_contacts")
    .select("member_id, flat_no, phone");

  if (error) throw new Error(error.message);
  return new Map((data as ContactRow[]).map((row) => [row.member_id, toContact(row)]));
}

/**
 * Save your own, as typed — the database cleans them and refuses what it will not keep, in the
 * words lib/contact.ts mirrors. Blank clears a field; both blank clears the lot.
 */
export async function saveContactDetails(
  flatNo: string | null,
  phone: string | null,
): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("set_contact_details", {
    new_flat_no: flatNo,
    new_phone: phone,
  });

  if (error) throw new Error(error.message);
}

export async function needsFoundingAdmin(): Promise<boolean> {
  if (!supabase) return false;

  const { data, error } = await supabase.rpc("community_needs_founding_admin");
  if (error) throw new Error(error.message);
  return data === true;
}

export async function claimFoundingAdmin(): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("claim_founding_admin");
  if (error) throw new Error(error.message);
}

/**
 * A plain update. Your own category you may set once, while it is empty; after that, or for
 * anybody else, it takes an admin — `guard_member_privileges` refuses the rest, because a
 * category now decides which events somebody may enter (20260930000100). RLS allows the row:
 * your own, or anyone's if you are an admin.
 */
export async function setMemberCategory(memberId: string, category: MemberCategory): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.from("members").update({ category }).eq("id", memberId);
  if (error) throw new Error(error.message);
}

/**
 * How you play, set on your own profile. A plain update, the same shape as `setMemberCategory`.
 * Only your own row: `guard_member_privileges` refuses a change to somebody else's, and an admin
 * changing anybody's goes through the audited RPC instead (`setMemberPlayingAs`). Both flags go
 * in one update because the constraint refuses a member who is both — moving a junior to senior
 * is one fact changing, not two writes (20261001000700).
 */
export async function setMemberPlayingAsSelf(memberId: string, as: PlayingAs): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.from("members").update(playingFlags(as)).eq("id", memberId);
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// Members without a login, and the people who look after them.
//
// Every write is an RPC: the database decides who may create a child, name a guardian or
// rename one, and a client that could write `members` or `guardians` directly for them
// would be deciding it instead. See 20260929000100.
// ---------------------------------------------------------------------------

// `guardians` holds two keys to `members` (and a third, `created_by`), so each embed names
// the one it means. Without the hint PostgREST refuses the query as ambiguous.
const CHILD = `member:members!guardians_member_id_fkey (${COLUMNS})`;
const GUARDIAN = `guardian:members!guardians_guardian_id_fkey (${COLUMNS})`;

/** The children this member looks after, by name. */
export async function fetchMyChildren(guardianId: string): Promise<Member[]> {
  if (!supabase) return demoChildrenOf(guardianId);

  const { data, error } = await supabase
    .from("guardians")
    .select(CHILD)
    .eq("guardian_id", guardianId);

  if (error) throw new Error(error.message);
  return (data as unknown as { member: MemberRow }[])
    .map((row) => toMember(row.member))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/**
 * Who looks after a child. Empty for a player who is not a guardian or organiser — RLS keeps
 * the link between their guardian and whoever reads it, so "nobody" and "not yours to know"
 * are the same answer here, as they are for `fetchMember`.
 */
export async function fetchGuardians(memberId: string): Promise<Member[]> {
  if (!supabase) return demoGuardiansOf(memberId);

  const { data, error } = await supabase
    .from("guardians")
    .select(GUARDIAN)
    .eq("member_id", memberId);

  if (error) throw new Error(error.message);
  return (data as unknown as { guardian: MemberRow }[])
    .map((row) => toMember(row.guardian))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

/**
 * Create a member without a login — always a junior, and always a boy or a girl, because
 * nothing but an open event could take a child with neither. A player becomes its guardian
 * and may not name anybody else; an organiser may name any member with a login, or nobody.
 * Returns the new id.
 */
export async function addManagedMember(
  name: string,
  category: MemberCategory,
  guardianId?: string | null,
): Promise<string> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase.rpc("add_managed_member", {
    member_name: name.trim(),
    child_category: category,
    guardian: guardianId ?? null,
  });

  if (error) throw new Error(error.message);
  return data as string;
}

/** A guardian or an admin fixing a child's name. The name is the only thing it can change. */
export async function renameManagedMember(memberId: string, name: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("rename_managed_member", {
    target: memberId,
    member_name: name.trim(),
  });

  if (error) throw new Error(error.message);
}

/** Organisers only, audited. False when they were already a guardian. */
export async function addGuardian(memberId: string, guardianId: string): Promise<boolean> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase.rpc("add_guardian", {
    target: memberId,
    guardian: guardianId,
  });

  if (error) throw new Error(error.message);
  return data === true;
}

/** Organisers only, audited. False when they were not a guardian to begin with. */
export async function removeGuardian(memberId: string, guardianId: string): Promise<boolean> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase.rpc("remove_guardian", {
    target: memberId,
    guardian: guardianId,
  });

  if (error) throw new Error(error.message);
  return data === true;
}

/**
 * Whether somebody plays as a junior, an adult or a senior. Admin only, and audited: it
 * decides which events they may enter, so it is a privilege rather than a profile field. One
 * call for both flags, so a junior becomes a senior in one step (20261001000200).
 */
export async function setMemberPlayingAs(memberId: string, as: PlayingAs): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("set_member_playing_as", {
    target: memberId,
    playing_as: as,
  });

  if (error) throw new Error(error.message);
}

/** Admin actions all go through one audited RPC — see the migration for why. */
export async function setMemberStatus(
  memberId: string,
  changes: { status?: MemberStatus; role?: MemberRole },
): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("set_member_status", {
    target: memberId,
    new_status: changes.status ?? null,
    new_role: changes.role ?? null,
  });

  if (error) throw new Error(error.message);
}
